/* eslint-disable arrow-body-style */
import React from 'react';
import _ from 'underscore';

import { NodeSelectedHandler } from '@bingads-webui-react/tree-view';

const segmentMaxLength = 1000;
const totalMaxLength = 4000;
const seperatorStr = ';;';

const types = ['FreeText', 'Selection', 'TreeSelection'];
// eslint-disable-next-line no-param-reassign
const Types = types.reduce((total, current) => { total[current] = current; return total; }, {});

const CategoryOperands = {
  CategoryL1: 'CategoryL1',
  CategoryL2: 'CategoryL2',
  CategoryL3: 'CategoryL3',
  CategoryL4: 'CategoryL4',
  CategoryL5: 'CategoryL5',
};

const DeepnessCategoryOperandMapping = {
  0: CategoryOperands.CategoryL1,
  1: CategoryOperands.CategoryL2,
  2: CategoryOperands.CategoryL3,
  3: CategoryOperands.CategoryL4,
  4: CategoryOperands.CategoryL5,
};

const CategoryOperandDeepnessMapping = {
  [CategoryOperands.CategoryL1]: 0,
  [CategoryOperands.CategoryL2]: 1,
  [CategoryOperands.CategoryL3]: 2,
  [CategoryOperands.CategoryL4]: 3,
  [CategoryOperands.CategoryL5]: 4,
};

// add entry here if we need to support tree view for other filters
const TreeConditionKeyDeepnessKeyMapping = {
  Category: DeepnessCategoryOperandMapping,
};

const renderChildern = (children, props) => {
  const childrenWithProps = React.Children.map(children, (child, index) =>
    React.cloneElement(child, { ...props, index }));

  return <React.Fragment>{childrenWithProps}</React.Fragment>;
};

// Necessity for tests to pass. String.replaceAll is not available in browser used for tests.
const replaceAll = (input, search, replace) => input.split(search).join(replace);

const multilineValueOutProcessor = (valueInput, seperator = ', ') => {
  const newStr = replaceAll(valueInput, ';;', '\n');

  const valueArray = newStr.split('\n').filter(e => e);

  let returnStr = valueArray[0].trim();

  for (let i = 1; i < valueArray.length; i += 1) {
    returnStr = `${returnStr}${seperator}${valueArray[i].trim()}`;
  }
  return returnStr;
};

const buildInValidationRulesImplementation = {
  NotEmpty: (value, data, type) => {
    if (type === Types.Selection) {
      return !_.isEmpty(_.result(data, 'valueKeys', []));
    }
    if (typeof value === 'string') {
      return value.replace(/\s/g, '').length !== 0;
    }
    return (typeof value === 'number');
  },
  TotalLength: (value) => {
    if (typeof value === 'string') {
      return value.replace(/\s/g, '').length <= totalMaxLength;
    }
    return (typeof value === 'number');
  },
  SegmentLength: (value) => {
    if (typeof value === 'string') {
      const valueArray = multilineValueOutProcessor(value, seperatorStr).replace(/\s/g, '').split(seperatorStr);

      return !_.any(valueArray, i => i.length > segmentMaxLength);
    }
    return (typeof value === 'number');
  },
  MaximumFiltersCount: (value, data, type, validateOptions) => {
    if (type === Types.TreeSelection) {
      return _.get(data, ['treeSelectionData', 'length']) <= validateOptions.allowedFiltersCount;
    }
    return true;
  },
};

/**
 * @type {Node}
 * @param {Node} node Node
 * @param {string} target identity to find the target
 * @param {function} getTargetCallBack this will be invoked when target is found
 * and then pass in the node
 * @param {function} getAncestorCallBack this will be invoked when target is found
 * and then pass in the ancestor node
 * @returns {boolean} found the target or not
 * @description DFS the target node and its ancestors
 */
const findTargetNodeAndAncestorsNodesRecursively = (
  node,
  target,
  getTargetCallBack = () => {},
  getAncestorCallBack = () => {}
) => {
  if (!node) {
    return false;
  }

  if (node.Text === target) {
    getTargetCallBack(node);
    return true;
  }

  if (node.Children && node.Children instanceof Array) {
    let isFound = false;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < node.Children.length; i++) {
      const child = node.Children[i];
      isFound = isFound ||
      findTargetNodeAndAncestorsNodesRecursively(
        child,
        target,
        getTargetCallBack,
        getAncestorCallBack
      );
      if (isFound) {
        getAncestorCallBack(node);
        return true;
      }
    }
  }

  return false;
};

const findTargetNodeAndAncestorsNodes = (
  node,
  target
) => {
  let foundTarget;
  const ancestors = [];
  findTargetNodeAndAncestorsNodesRecursively(
    node, target, (t) => {
      foundTarget = t;
    },
    (ancs) => {
      ancestors.push(ancs);
    }
  );

  return {
    target: foundTarget,
    parents: ancestors.reverse(),
  };
};

const getNodesCountRecursively = (root) => {
  const selfCount = 1;
  const childCount = _.reduce(root.Children, (sum, item) => {
    return sum + getNodesCountRecursively(item);
  }, 0);

  return childCount + selfCount;
};

const setNodesExpandState = (nodes) => {
  nodes.forEach((node) => {
    // eslint-disable-next-line no-param-reassign
    node.state.expanded = !!node.state.indeterminate;
    setNodesExpandState(node.children);
  });
};

const buildTreeNodesFromValueOptions = (valueOptions) => {
  let id = 0;
  // eslint-disable-next-line no-return-assign
  const getId = () => (id += 1);
  const nodeToSelect = [];
  const buildTreeNodes = (nodes, parentNode) => {
    if (!nodes || !nodes.length) {
      return [];
    }
    return nodes.map((node) => {
      const newNode = {
        id: getId(),
        name: node.Text,
        key: node.Text,
        state: {
          expanded: false,
          checked: node.Selected,
        },
      };
      newNode.parents = parentNode ? [...parentNode.parents, parentNode.id] : [];
      newNode.deepness = newNode.parents.length;
      newNode.children = buildTreeNodes(node.Children, newNode);
      if (node.Selected) {
        nodeToSelect.push(newNode);
      }
      return newNode;
    });
  };

  const nodes = buildTreeNodes(valueOptions);
  // set node indeterminate state
  nodeToSelect.forEach(selectedNode => NodeSelectedHandler(nodes, selectedNode));
  setNodesExpandState(nodes);
  return nodes;
};

const getInitTreeNodes = (type, valueOptions) => {
  if (type === Types.TreeSelection) {
    return buildTreeNodesFromValueOptions(valueOptions);
  }
  return null;
};

const buildTreeSelectionData = (selectedNodes, deepnessKeyMapping) => {
  return _.chain(selectedNodes)
    .groupBy('deepness')
    .map((nodes, deepness) => {
      return {
        key: deepnessKeyMapping[deepness],
        valueKeys: _.map(nodes, 'key'),
      };
    })
    .value();
};

const isTreeData = (data) => {
  if (_.isArray(data) && data.length > 0) {
    return !_.isEmpty(data[0].Children);
  }
  return false;
};

const keyExtractor = item => item.Text;

const MTToUIValueOptionsMapper = (item) => {
  return {
    key: keyExtractor(item),
    text: item.Text,
  };
};

export {
  types,
  Types,
  renderChildern,
  buildInValidationRulesImplementation,
  findTargetNodeAndAncestorsNodes,
  getNodesCountRecursively,
  multilineValueOutProcessor,
  buildTreeNodesFromValueOptions,
  getInitTreeNodes,
  keyExtractor,
  MTToUIValueOptionsMapper,
  CategoryOperands,
  CategoryOperandDeepnessMapping,
  DeepnessCategoryOperandMapping,
  TreeConditionKeyDeepnessKeyMapping,
  buildTreeSelectionData,
  isTreeData,
};
